import { useMetadata } from '@/hooks/useMetadata';
import { Severity } from '@/types/validation';
import { VariableScope } from '@/types/variable';
import { isEmptyValue, isNullOrUndefined } from '@/util/typeGuards';
import { getScopeLabel } from '@/util/variables';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CancelIcon from '@mui/icons-material/Cancel';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import CloseIcon from '@mui/icons-material/Close';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import RouteIcon from '@mui/icons-material/Route';
import SettingsPhoneIcon from '@mui/icons-material/SettingsPhone';
import TaskIcon from '@mui/icons-material/Task';
import { Box, Checkbox, Chip, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { FC } from 'react';

type VariableProps = {
  label?: string;
  value: any;
  chipText?: string | number;
  type?: string;
  variableType?: string;
  scope?: VariableScope;
  onClick?: () => void;
  onDelete?: () => void;
  allowEmpty?: boolean;
  onEmptyClick?: () => void;
  required?: boolean;
  error?: Severity;
};

const VariableChip: FC<VariableProps> = (props) => {
  const { metadata } = useMetadata();
  const theme = useTheme();
  const contrastColor = theme.palette.mode === 'dark' ? 'white' : 'black';

  const getScopeIcon = () => {
    switch (props.scope) {
      case 'global':
        return <PublicIcon />;
      case 'task':
        return <TaskIcon />;
      case 'session':
        return <SettingsPhoneIcon />;
      case 'channel':
        return <RouteIcon />;
      case 'local':
        return <PlaceIcon />;
      default:
        return <DataObjectIcon />;
    }
  };

  return (
    <Stack
      direction='row'
      alignItems='center'
      gap={1}
      width='100%'
      color={props.error ? props.error + '.main' : contrastColor}
    >
      {props.required && <Typography variant='body1'>*</Typography>}
      <Typography variant='caption'>{props.label}</Typography>
      {isNullOrUndefined(props.value) && (
        <Typography variant='body1' fontStyle='italic' color='text.disabled'>
          No variable set
        </Typography>
      )}
      <Box sx={{ flexGrow: 1, visibility: isEmptyValue(props.value) ? 'hidden' : 'visible' }}>
        <Tooltip
          title={
            (props.scope || props.variableType) && (
              <Box>
                {props.scope && <Box>{`Scope: ${getScopeLabel(props.scope, metadata)}`}</Box>}
                {props.variableType && <Box>{`Type: ${props.variableType}`}</Box>}
              </Box>
            )
          }
          placement='top'
          arrow
        >
          <Chip
            icon={getScopeIcon()}
            deleteIcon={props.chipText ? <CloseIcon fontSize='small' /> : <ArrowDropDownIcon />}
            label={props.chipText || 'Pick one'}
            onDelete={props.chipText ? props.onDelete : props.onClick}
            onClick={props.onClick}
            sx={{
              fontFamily: 'monospace',
              fontSize: '1em',
              fontWeight: 700,
              pl: 0.5,
              pr: 0.5
            }}
            size='small'
            color='secondary'
          />
        </Tooltip>
      </Box>
      {props.allowEmpty && !props.chipText && (
        <Tooltip title={'Ignore value'} placement='right' arrow>
          <Checkbox
            icon={<CancelIcon />}
            checkedIcon={<CancelPresentationIcon />}
            checked={isEmptyValue(props.value)}
            onChange={(_, checked) =>
              props.onEmptyClick && checked ? props.onEmptyClick() : props.onDelete && props.onDelete()
            }
            color='default'
          />
        </Tooltip>
      )}
      <code>{props.type}</code>
    </Stack>
  );
};

export default VariableChip;
